import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';

interface BalanceCheckResult {
  calculatedBalance: number;
  isDivergent: boolean;
  difference: number;
  isLoading: boolean;
}

// Régua única de divergência: get_account_balances (catálogo) deriva o saldo
// no servidor (inicial + receitas - despesas + transferências) e devolve a
// divergência contra o saldo armazenado. Nenhum cálculo client.
export const useAccountBalanceCheck = (accountId: string): BalanceCheckResult => {
  const { companyId } = useMultiTenant();

  const { data, isLoading } = useQuery({
    queryKey: ['account-balance-check', accountId, companyId],
    queryFn: async () => {
      const { data: rows, error } = await supabase.rpc('get_account_balances');
      if (error) throw error;
      return (rows || []).find(r => r.account_id === accountId) ?? null;
    },
    enabled: !!companyId && !!accountId,
  });

  const divergence = data?.divergence ?? 0;

  return {
    calculatedBalance: data?.derived_balance ?? 0,
    isDivergent: Math.abs(divergence) > 0.01,
    difference: divergence,
    isLoading,
  };
};

// Hook para verificar divergência de múltiplas contas (para a tabela)
export const useAccountsBalanceCheck = (accounts: Array<{ id: string }>) => {
  const { companyId } = useMultiTenant();

  const { data, isLoading } = useQuery({
    queryKey: ['accounts-balance-check', companyId, accounts.map(a => a.id).join(',')],
    queryFn: async () => {
      const { data: rows, error } = await supabase.rpc('get_account_balances');
      if (error) throw error;

      const requestedIds = new Set(accounts.map(a => a.id));
      const result: Record<string, { calculatedBalance: number; isDivergent: boolean; difference: number }> = {};

      (rows || []).forEach(row => {
        if (!requestedIds.has(row.account_id)) return;
        result[row.account_id] = {
          calculatedBalance: row.derived_balance,
          isDivergent: Math.abs(row.divergence) > 0.01,
          difference: row.divergence,
        };
      });

      return result;
    },
    enabled: !!companyId && accounts.length > 0,
  });

  return { balanceChecks: data || {}, isLoading };
};
